import { useState, useEffect } from "react"
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react"
import {
  XMarkIcon,
  PaperAirplaneIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  PaperClipIcon,
  PlusIcon,
  EyeIcon,
} from "@heroicons/react/24/outline"
import { Fragment } from "react"
import { Button, ScrollShadow, Avatar, Badge, Spinner } from "components/ui"
import { Input, Textarea } from "components/ui/Form"
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from "@headlessui/react"
import { getTemplates, extractTemplateVariables, replaceTemplateVariables, htmlToPlainText } from "services/templateService"
import { sendEmailToLead, sendEmailToMultipleLeads } from "services/leadsService"
import { sendEmailToJob, sendEmailToMultipleJobs, sendEmailToJobUsers } from "services/jobsService"
import { getIntegrationsByType } from "services/integrationService"
import { RichTextEditor } from "components/shared/RichTextEditor"
import { ManageVariablesModal } from "components/shared/ManageVariablesModal"
import { EmailServiceSelector } from "components/shared/EmailServiceSelector"
import { toast } from "sonner"
import { ensureCompleteEmailHtml } from "utils/emailHtmlWrapper"
import { EmailScheduleToggle } from "./EmailScheduleToggle"
import ViewDocumentModal from "components/leads/LeadDetails/DocumentsTab/components/ViewDocumentModal"
import { useAuthContext } from "app/contexts/auth/context";
import { ConfirmModal } from "components/shared/ConfirmModal"

// Email validation utility
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

// File size formatter utility
const formatFileSize = (bytes) => {
  if (!bytes) return "0.0 KB"
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

// Calculate total attachments size helper
const getAttachmentsTotalSize = (attachments) => {
  return attachments.reduce((sum, f) => {
    const size = f.size || f.FileSize || f.fileSize || 0
    return sum + Number(size)
  }, 0)
}

// Helper to extract contacts array from lead info
const getContactsForLead = (lead) => {
  if (!lead) return []
  let leadContacts = lead.contacts || lead.LeadContact || lead.LeadContacts || lead.ContactDetail || lead.contactDetail
  if (!leadContacts && lead.leadinfo) {
    try {
      const leadinfo = typeof lead.leadinfo === 'string' ? JSON.parse(lead.leadinfo) : lead.leadinfo
      leadContacts = leadinfo?.contacts || leadinfo?.LeadContact || leadinfo?.LeadContacts || leadinfo?.ContactDetail || leadinfo?.contactDetail
    } catch (e) { /* ignore */ }
  }
  if (typeof leadContacts === 'string' && leadContacts) {
    try {
      leadContacts = JSON.parse(leadContacts)
    } catch (e) { /* ignore */ }
  }
  if (Array.isArray(leadContacts) && leadContacts.length > 0) return leadContacts;
  if (lead.email || lead.Email) return [lead];
  return []
}

// Safe iframe-based HTML preview to avoid leaking template CSS into app
const IframePreview = ({ html, height = 160 }) => {
  const srcDoc = `<!DOCTYPE html>
  <html>
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <style>
        html, body { margin: 0; padding: 0; background: transparent; }
        body { font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"; color: #111827; font-size: 14px; line-height: 1.5; }
        h1 { font-size: 1.25rem; font-weight: 700; margin-bottom: 0.5rem; }
        h2 { font-size: 1.125rem; font-weight: 600; margin-bottom: 0.5rem; }
        h3 { font-size: 1rem; font-weight: 600; margin-bottom: 0.5rem; }
        a { color: #2563EB; }
        * { box-sizing: border-box; max-width: 100%; }
        @media (prefers-color-scheme: dark) {
          body, body * { color: #ffffff !important; }
          a, a * { color: #60a5fa !important; }
        }
      </style>
    </head>
    <body>${html || ""}</body>
  </html>`

  return (
    <iframe
      title="email-template-preview"
      srcDoc={srcDoc}
      sandbox="allow-popups-to-escape-sandbox"
      className="w-full rounded-md border border-gray-200 dark:border-gray-700 bg-transparent"
      style={{ height }}
    />
  )
}

// Email Chips Input Component
const EmailChipsInput = ({ label, emails, onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState("")
  const [inputError, setInputError] = useState("")

  const addEmail = (email) => {
    const trimmedEmail = email.trim()
    if (!trimmedEmail) return

    if (!isValidEmail(trimmedEmail)) {
      setInputError("Please enter a valid email address")
      return
    }

    if (emails.includes(trimmedEmail)) {
      setInputError("Email address already added")
      return
    }

    onChange([...emails, trimmedEmail])
    setInputValue("")
    setInputError("")
  }

  const removeEmail = (emailToRemove) => {
    onChange(emails.filter(email => email !== emailToRemove))
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addEmail(inputValue)
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      // Remove last email when backspace is pressed on empty input
      removeEmail(emails[emails.length - 1])
    }
  }

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      addEmail(inputValue)
    }
  }



  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>

      <div className={`min-h-[42px] p-2 border rounded-lg bg-white dark:bg-gray-800 transition-colors ${inputError
        ? 'border-red-300 dark:border-red-600 focus-within:ring-2 focus-within:ring-red-500 focus-within:border-red-500'
        : 'border-gray-300 dark:border-gray-600 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500'
        }`}>
        <div className="flex flex-wrap gap-2 items-center">
          {/* Email Chips */}
          {emails.map((email, index) => (
            <div
              key={index}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded-lg text-sm font-medium border border-blue-200 dark:border-blue-700 transition-all duration-200 animate-in fade-in slide-in-from-left-2 max-w-full"
            >
              <EnvelopeIcon className="w-3.5 h-3.5 opacity-70 shrink-0" />
              <span className="truncate">{email}</span>
              <button
                type="button"
                onClick={() => removeEmail(email)}
                className="ml-0.5 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 shrink-0"
                title="Remove email"
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            </div>
          ))}

          {/* Input Field */}
          <input
            type="email"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value)
              setInputError("")
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleInputBlur}
            placeholder={emails.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
          />
        </div>
      </div>

      {inputError && (
        <p className="text-sm text-red-600 dark:text-red-400">{inputError}</p>
      )}

      <div className="flex justify-between items-center">
        {emails.length > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {emails.length} email{emails.length !== 1 ? 's' : ''} added
          </span>
        )}
      </div>
    </div>
  )
}

const EmailMessageModal = ({
  isOpen,
  onClose,
  leadData,
  selectedLeads = null,
  initialAttachments = [],
  autoSelectEmails = true,
  hideBulkLabels = false,
  isJobContext = false,
  isJobUsersContext = false,
  hideSchedule = false,
  initialSubject = "",
  initialBody = "",
  initialTemplateVariables = {},
  confirmOnCancel = false,
  discardWarningMessage = "You haven't sent this email yet. Are you sure you want to close without sending?"
}) => {
  // Simple states for email sending
  const [activeTab, setActiveTab] = useState(0) // 0 = Compose, 1 = Templates
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [templateVariables, setTemplateVariables] = useState(initialTemplateVariables || {})
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [templateSearch, setTemplateSearch] = useState("")
  const [isSending, setIsSending] = useState(false)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [showHtmlEditor, setShowHtmlEditor] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  // Email service selection states
  const [selectedEmailService, setSelectedEmailService] = useState(null)
  const [showEmailServiceSelector, setShowEmailServiceSelector] = useState(false)

  // Email form data
  const [emailData, setEmailData] = useState({
    to: leadData?.email || "",
    toEmails: [], // For bulk mode - array of email addresses
    cc: [],
    bcc: [],
    subject: "",
    body: "",
    attachments: [] // Array of file objects or URLs
  })

  // Toggle states for CC/BCC
  const [showCc, setShowCc] = useState(false)
  const [showBcc, setShowBcc] = useState(false)

  const { user } = useAuthContext();
  const currentUser = user?.FullName || null;
  const uploadedBy = currentUser || "Current User";

  // Centralized Modal State Reset
  const resetModalState = () => {
    setSelectedTemplate(null)
    setTemplateVariables({})
    setActiveTab(0)
    setShowHtmlEditor(false)
    setShowCc(false)
    setShowBcc(false)
    setScheduleForLater(false)
    setScheduledDate("")
    setScheduledTime("")
    setShowImageModal(false)
    setImageUrl("")
    setImageWidth("300")
    setImageHeight("200")
    setShowHtmlModal(false)
    setHtmlContent("")
    setHtmlContentToInsert("")
    setEditingImage(null)
    setSelectedEmailService(null)
    setIsDragging(false)
  }

  // Document preview modal states
  const [viewDocModalOpen, setViewDocModalOpen] = useState(false)
  const [selectedDocToView, setSelectedDocToView] = useState(null)

  const handleViewAttachment = (file) => {
    let docToView = file;
    if (file instanceof File || file instanceof Blob) {
      docToView = {
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedBy: uploadedBy,
        createdAt: new Date(),
        previewUrl: URL.createObjectURL(file),
        rawFile: file,
      };
    } else if (typeof file === "object" && file !== null) {
      const filePathKey = file.FilePath || file.filePath || file.path || file.FileKey || file.fileKey;
      docToView = {
        ...file,
        name: file.name || file.FileName || file.fileName || "document",
        FilePath: filePathKey,
        filePath: filePathKey,
        FileKey: filePathKey,
        fileKey: filePathKey,
        uploadedBy: file.uploadedBy || file.createdBy || uploadedBy,
        createdAt: file.createdAt || file.uploadedAt || file.createdDate || file.CreatedOn || new Date(),
      };
    }
    setSelectedDocToView(docToView)
    setViewDocModalOpen(true)
  }

  // File upload and drag-and-drop handlers
  const handleAddFiles = (incomingFiles) => {
    const files = Array.from(incomingFiles || [])
    if (files.length === 0) return

    // Filter out duplicate files based on file name and size
    const newFiles = files.filter(f => !emailData.attachments.some(a => {
      const aName = a.name || a.FileName || a.fileName
      const aSize = a.size !== undefined ? a.size : (a.FileSizeBytes !== undefined ? a.FileSizeBytes : a.fileSizeBytes)
      return aName === f.name && aSize === f.size
    }))

    if (newFiles.length === 0) {
      toast.info("Selected file(s) are already attached")
      return
    }

    const MAX_SIZE = 20 * 1024 * 1024 // 20 MB
    const currentSize = getAttachmentsTotalSize(emailData.attachments)
    const newFilesSize = newFiles.reduce((sum, f) => sum + (f.size || 0), 0)

    if (currentSize + newFilesSize > MAX_SIZE) {
      toast.error(`Total attachments size cannot exceed 20 MB. Total size would be ${formatFileSize(currentSize + newFilesSize)}.`)
      return
    }

    setEmailData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...newFiles]
    }))
    toast.success(`${newFiles.length} file${newFiles.length !== 1 ? 's' : ''} added`)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'copy'
    if (!isDragging) setIsDragging(true)
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'copy'
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.currentTarget.contains(e.relatedTarget)) return
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleAddFiles(e.dataTransfer.files)
    }
  }

  // Scheduling states
  const [scheduleForLater, setScheduleForLater] = useState(false)
  const [scheduledDate, setScheduledDate] = useState("")
  const [scheduledTime, setScheduledTime] = useState("")

  // Rich text editor states
  const [showImageModal, setShowImageModal] = useState(false)
  const [imageUrl, setImageUrl] = useState("")
  const [imageWidth, setImageWidth] = useState("300")
  const [imageHeight, setImageHeight] = useState("200")
  const [showHtmlModal, setShowHtmlModal] = useState(false)
  const [showVariablesModal, setShowVariablesModal] = useState(false)
  const [htmlContent, setHtmlContent] = useState("")
  const [htmlContentToInsert, setHtmlContentToInsert] = useState("")

  // Add useEffect to automatically clear htmlContentToInsert after insertion
  useEffect(() => {
    if (htmlContentToInsert) {
      // Clear after a short delay to allow the RichTextEditor to process it
      const timer = setTimeout(() => {
        setHtmlContentToInsert("")
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [htmlContentToInsert])
  const [editingImage, setEditingImage] = useState(null)

  // Helper to parse lead info safely
  const getLeadParsedData = (lead) => {
    if (!lead) return {};
    let parsed = {};
    if (lead.leadinfo && typeof lead.leadinfo === "string") {
      try {
        parsed = JSON.parse(lead.leadinfo);
      } catch (e) { /* ignore */ }
    } else if (lead.leadinfo && typeof lead.leadinfo === "object") {
      parsed = lead.leadinfo;
    }

    const name = lead.FullName || lead.name || parsed.FullName || lead.firstName || "Unknown";
    const email = lead.Email || lead.email || parsed.Email || lead.EmailAddress || lead.emailAddress || "";
    const phone = lead.PhoneNumber || lead.phone || parsed.PhoneNumber || lead.cellPhone || lead.mobile || "";

    return { name, email, phone };
  };

  // Determine if this is bulk email mode
  const isBulkMode = !isJobUsersContext && selectedLeads && selectedLeads.length > 0
  // Check multiple email field variations
  const validLeads = (isBulkMode || isJobUsersContext)
    ? selectedLeads.flatMap(lead => {
      const leadId = lead.LeadId || lead.id || lead.JobId || lead.UserId;

      const leadContacts = getContactsForLead(lead);

      if (Array.isArray(leadContacts) && leadContacts.length > 0) {
        const mappedContacts = leadContacts
          .map(contact => {
            const email = contact.Email || contact.email || "";
            const name = contact.Fullname || contact.FullName || contact.fullName || contact.UserName || contact.userName || "Unknown";
            const phone = contact.PhoneNumber || contact.phoneNumber || contact.Phone || contact.phone || "";
            return {
              ...lead,
              id: leadId,
              _parsedEmail: email.trim(),
              _parsedName: name,
              _parsedPhone: phone,
              _contactId: contact.ContactId || contact.contactId || contact.UserId || contact.userId
            };
          })
          .filter(c => c._parsedEmail && c._parsedEmail !== "N/A" && c._parsedEmail.trim() !== "");

        if (mappedContacts.length > 0) {
          return mappedContacts;
        }
      }

      // If no valid contacts/emails are found, map to "unknown | no email"
      return [{
        ...lead,
        id: leadId,
        _parsedEmail: (lead.Email || lead.email || "").trim() || "unknown | no email",
        _parsedName: lead.clientContact || lead.title || lead.FullName || lead.UserName || "Unknown",
        _parsedPhone: lead.PhoneNumber || lead.phone || ""
      }];
    })
    : []

  // Get contacts for the active lead(s)
  const leadContacts = (isBulkMode || isJobUsersContext)
    ? (selectedLeads || []).flatMap(lead => getContactsForLead(lead))
    : getContactsForLead(leadData);

  const contactEmails = leadContacts.map(c => (c.Email || c.email || "").trim().toLowerCase()).filter(Boolean);

  const hasNonContactEmail = isBulkMode
    ? emailData.toEmails.some(email => {
      const trimmed = email.trim().toLowerCase();
      return trimmed && !contactEmails.includes(trimmed);
    })
    : (() => {
      const trimmed = emailData.to.trim().toLowerCase();
      return trimmed && isValidEmail(trimmed) && !contactEmails.includes(trimmed);
    })();


  // Load templates when modal opens
  useEffect(() => {
    if (isOpen) {
      loadTemplates()
      resetModalState()

      const allContactEmails = !isBulkMode
        ? leadContacts.map(c => c.Email || c.email || "").filter(Boolean)
        : [];

      if (!isBulkMode && leadData?.email && !allContactEmails.includes(leadData.email)) {
        allContactEmails.unshift(leadData.email);
      }

      const initialToValue = (isBulkMode || isJobUsersContext) && autoSelectEmails
        ? `${validLeads.length} recipients selected`
        : (allContactEmails.length === 1 ? allContactEmails[0] : (allContactEmails.length > 1 ? `${allContactEmails.length} recipients selected` : ""));

      const initialToEmails = (isBulkMode || isJobUsersContext)
        ? (autoSelectEmails ? validLeads.map(lead => lead._parsedEmail).filter(email => email && email !== "N/A" && email !== "unknown | no email") : [])
        : allContactEmails;

      setEmailData({
        to: initialToValue,
        toEmails: initialToEmails,
        cc: [],
        bcc: [],
        subject: initialSubject || "",
        body: initialBody || "",
        attachments: initialAttachments || []
      })
      if (Object.keys(initialTemplateVariables || {}).length > 0) {
        setTemplateVariables(initialTemplateVariables);
      } else if (!isBulkMode && leadData) {
        const primaryContact = allContactEmails.length > 0
          ? (leadContacts.find(c => (c.Email || c.email || "").trim().toLowerCase() === allContactEmails[0]?.trim().toLowerCase()) || leadContacts[0])
          : (leadContacts[0] || null);
        const cName = primaryContact?.FullName || primaryContact?.Fullname || primaryContact?.fullName || primaryContact?.UserName || primaryContact?.userName || primaryContact?.name || leadData?.FullName || leadData?.fullName || leadData?.name || "";
        const cEmail = primaryContact?.Email || primaryContact?.email || leadData?.email || leadData?.Email || "";
        const cPhone = primaryContact?.PhoneNumber || primaryContact?.phoneNumber || primaryContact?.phone || primaryContact?.Phone || leadData?.phoneNumber || leadData?.phone || "";
        const lName = leadData?.FullName || leadData?.fullName || leadData?.name || cName || "";
        const lEmail = leadData?.email || leadData?.Email || cEmail || "";
        const lPhone = leadData?.phoneNumber || leadData?.phone || cPhone || "";

        setTemplateVariables({
          FullName: cName || lName,
          Name: cName || lName,
          ContactName: cName || lName,
          LeadName: lName,
          Email: cEmail || lEmail,
          ContactEmail: cEmail || lEmail,
          LeadEmail: lEmail,
          PhoneNumber: cPhone || lPhone,
          Phone: cPhone || lPhone,
          Mobile: cPhone || lPhone,
          ContactPhone: cPhone || lPhone,
          LeadPhone: lPhone,
          CompanyName: leadData?.companyName || leadData?.CompanyName || user?.CompanyName || "",
          CurrentYear: new Date().getFullYear().toString(),
          CurrentDate: new Date().toISOString().split("T")[0],
        });
      }
    } else {
      // Clean up search timeout when modal closes
      if (window.searchTimeout) {
        clearTimeout(window.searchTimeout)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Rich text editor helper functions
  const handleImageInsert = () => {
    if (imageUrl.trim()) {
      setShowImageModal(false)
      setTimeout(() => {
        setImageUrl("")
        setImageWidth("300")
        setImageHeight("200")
        setEditingImage(null)
      }, 100)
    }
  }

  const handleImageModalClose = () => {
    setShowImageModal(false)
    setImageUrl("")
    setImageWidth("300")
    setImageHeight("200")
    setEditingImage(null)
  }

  const handleImageEdit = (imageNode) => {
    setEditingImage(imageNode)
    setImageUrl(imageNode.attrs.src)
    setImageWidth(imageNode.attrs.width || "300")
    setImageHeight(imageNode.attrs.height || "200")
    setShowImageModal(true)
  }

  const handleHtmlInsert = () => {
    if (htmlContent.trim()) {
      // Trigger insertion by setting the content to insert
      setHtmlContentToInsert(htmlContent)
      // Close the modal and clear the modal content
      setShowHtmlModal(false)
      setHtmlContent("")
    }
  }

  const handleHtmlModalClose = () => {
    setShowHtmlModal(false)
    setHtmlContent("")
  }

  const loadTemplates = async (searchTerm = templateSearch) => {
    try {
      setTemplatesLoading(true)
      const result = await getTemplates({
        pageNumber: 1,
        pageSize: 50,
        channelId: 1, // Email channel ID (ChannelId: 1 is Email)
        search: searchTerm
      })
      if (result.success) {
        setTemplates(result.data)
      } else {
        toast.error(result.error || "Failed to load templates")
      }
    } catch (error) {
      console.error("Error loading templates:", error)
      toast.error("Failed to load templates")
    } finally {
      setTemplatesLoading(false)
    }
  }

  const handleTemplateSearch = (searchTerm) => {
    setTemplateSearch(searchTerm)
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout)
    }
    window.searchTimeout = setTimeout(() => {
      loadTemplates(searchTerm)
    }, 300)
  }

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template)
    setActiveTab(0) // Switch to compose tab

    const templateBody = template.body || template.Body || ''
    const templateSubject = template.subject || template.Subject || ''

    setEmailData(prev => ({
      ...prev,
      subject: templateSubject,
      body: templateBody
    }))

    // Automatically include template attachments if they exist
    if (template.attachments && template.attachments.length > 0) {
      // Map attachment objects to a format compatible with the file input
      const mappedAttachments = template.attachments.map(att => {
        // If it's already a File object, use it directly
        if (att instanceof File) {
          return att;
        }

        // Otherwise, create a reference object that can be used for display
        // Note: For actual file uploads, you may need to fetch the file from the URL
        const filePathKey = att.FilePath || att.filePath || att.path || att.FileKey || att.fileKey;
        return {
          name: att.FileName || att.fileName || att.name || 'Unknown File',
          path: filePathKey,
          FilePath: filePathKey,
          filePath: filePathKey,
          FileKey: filePathKey,
          fileKey: filePathKey,
          size: Number(att.FileSize || att.fileSize || att.FileSizeBytes || att.fileSizeBytes || 0),
          templateAttachment: true // Flag to identify template attachments
        };
      });

      const MAX_SIZE = 20 * 1024 * 1024; // 20 MB
      const currentSize = getAttachmentsTotalSize(emailData.attachments);
      const templateFilesSize = getAttachmentsTotalSize(mappedAttachments);

      if (currentSize + templateFilesSize > MAX_SIZE) {
        toast.error("Adding template attachments would exceed the 20 MB size limit");
        // Add only allowed attachments
        let sizeAccumulator = currentSize;
        const allowedAttachments = [];
        for (const att of mappedAttachments) {
          const size = att.size || 0;
          if (sizeAccumulator + size <= MAX_SIZE) {
            allowedAttachments.push(att);
            sizeAccumulator += size;
          } else {
            toast.warning(`Skipped template attachment "${att.name}" because it exceeds the 20 MB limit`);
          }
        }
        if (allowedAttachments.length > 0) {
          setEmailData(prev => ({
            ...prev,
            attachments: [...prev.attachments, ...allowedAttachments]
          }));
          toast.success(`Template selected with ${allowedAttachments.length} attachment${allowedAttachments.length !== 1 ? 's' : ''}`);
        }
      } else {
        setEmailData(prev => ({
          ...prev,
          attachments: [...prev.attachments, ...mappedAttachments]
        }));
        toast.success(`Template selected with ${mappedAttachments.length} attachment${mappedAttachments.length !== 1 ? 's' : ''}`);
      }
    }
  }




  const proceedWithEmailSend = async (emailService) => {
    if (isJobUsersContext) {
      if (emailData.toEmails.length === 0) {
        toast.error("Please enter recipient email")
        return
      }

      try {
        setIsSending(true)

        const resolvedSubject = replaceTemplateVariables(emailData.subject, templateVariables);
        const resolvedBody = replaceTemplateVariables(emailData.body, templateVariables);
        const completeEmailHtml = ensureCompleteEmailHtml(resolvedBody)

        const recipients = emailData.toEmails.map(email => {
          const matched = leadContacts.find(c => (c.Email || c.email || "").trim().toLowerCase() === email.trim().toLowerCase());
          const userId = matched?.UserId || matched?.userId || matched?.ExternalUserId || matched?.externalUserId || matched?.InternalUserId || matched?.internalUserId || matched?.ContactId || matched?.id || leadData?.UserId || leadData?.userId || leadData?.ExternalUserId || leadData?.InternalUserId || null;
          return { userId, email };
        });

        const result = await sendEmailToJobUsers(
          leadData?.id || leadData?.JobId || null,
          recipients,
          resolvedSubject,
          completeEmailHtml,
          emailData.cc,
          emailData.bcc,
          emailData.attachments,
          emailService?.ProviderCode,
          selectedTemplate?.TemplateId || selectedTemplate?.templateId || null,
          scheduleForLater ? scheduledDate : null,
          scheduleForLater ? scheduledTime : null
        );

        if (result.success) {
          toast.success(result.message || (scheduleForLater ? "Email scheduled successfully!" : "Email sent successfully!"))
          resetModalState()
          onClose(true)
        } else {
          throw new Error(result.message || (scheduleForLater ? "Failed to schedule email" : "Failed to send email"))
        }
      } catch (error) {
        console.error("Error sending email to job users:", error)
        toast.error(error.message || "Failed to send email")
      } finally {
        setIsSending(false)
      }
      return;
    }

    if (isBulkMode) {
      // Bulk email mode
      if (emailData.toEmails.length === 0) {
        toast.error("No email addresses selected")
        return
      }

      // Filter out non-contact emails
      const filteredToEmails = emailData.toEmails.filter(email => {
        const trimmed = email.trim().toLowerCase();
        return contactEmails.includes(trimmed);
      });

      if (filteredToEmails.length === 0) {
        toast.error("No valid lead contact emails selected")
        return
      }

      try {
        setIsSending(true)

        // Transform email addresses to match expected format
        // Find corresponding lead data for each email
        const leadsForEmail = filteredToEmails.map(email => {
          const lead = validLeads.find(l => {
            const leadEmail = l._parsedEmail
            return leadEmail === email
          })
          return {
            id: lead?.LeadId || email, // Fallback to email if lead not found
            email: email,
            name: lead?._parsedName || email.split('@')[0],
            _contactId: lead?._contactId
          }
        })

        // Get matching contactIds for the filtered emails
        const contactIds = filteredToEmails.map(email => {
          const contact = leadContacts.find(c => (c.Email || c.email || "").trim().toLowerCase() === email.trim().toLowerCase());
          return contact ? (contact.ContactId || contact.Id || contact.id) : null;
        }).filter(Boolean);

        // Wrap body HTML in complete HTML document with head tags and styling
        const resolvedSubject = replaceTemplateVariables(emailData.subject, templateVariables);
        const resolvedBody = replaceTemplateVariables(emailData.body, templateVariables);
        const completeEmailHtml = ensureCompleteEmailHtml(resolvedBody)

        const result = await (isJobContext ? sendEmailToMultipleJobs : sendEmailToMultipleLeads)(
          leadsForEmail,
          resolvedSubject,
          completeEmailHtml,
          emailData.cc,
          emailData.bcc,
          emailData.attachments,
          emailService?.ProviderCode,
          selectedTemplate?.TemplateId || selectedTemplate?.templateId || null,
          scheduleForLater ? scheduledDate : null,
          scheduleForLater ? scheduledTime : null,
          contactIds
        )

        if (result.success) {
          toast.success(result.message || (scheduleForLater ? `Email scheduled for ${filteredToEmails.length} recipients successfully!` : `Email sent to ${filteredToEmails.length} recipients successfully!`))
          resetModalState()
          onClose(true)
        } else {
          throw new Error(result.message || (scheduleForLater ? "Failed to schedule emails" : "Failed to send emails"))
        }
      } catch (error) {
        console.error("Error sending bulk email:", error)
        let displayError = error.message || (scheduleForLater ? "Failed to schedule emails" : "Failed to send emails")
        const responseData = error.data || error.response?.data
        if (responseData?.data) {
          try {
            const nestedData = typeof responseData.data === 'string'
              ? JSON.parse(responseData.data)
              : responseData.data
            if (nestedData?.Errors && Array.isArray(nestedData.Errors) && nestedData.Errors.length > 0) {
              displayError = nestedData.Errors.join(', ')
            } else if (nestedData?.errors && Array.isArray(nestedData.errors) && nestedData.errors.length > 0) {
              displayError = nestedData.errors.join(', ')
            }
          } catch (e) {
            // ignore
          }
        } else if (responseData?.errors && typeof responseData.errors === 'object') {
          const errorKeys = Object.keys(responseData.errors)
          if (errorKeys.length > 0) {
            const firstErrorKey = errorKeys[0]
            const errorsForField = responseData.errors[firstErrorKey]
            if (Array.isArray(errorsForField) && errorsForField.length > 0) {
              displayError = errorsForField[0]
            } else if (typeof errorsForField === 'string') {
              displayError = errorsForField
            }
          }
        }
        toast.error(displayError)
      } finally {
        setIsSending(false)
      }
    } else {
      // Single email mode
      if (emailData.toEmails.length === 0) {
        toast.error("Please enter recipient email")
        return
      }

      if (!leadData?.id) {
        toast.error(isJobContext ? "Job ID is required to send email" : "Lead ID is required to send email")
        return
      }

      // Map each selected email to its matching contact
      const selectedContacts = emailData.toEmails.map(email => {
        const matched = leadContacts.find(c => (c.Email || c.email || "").trim().toLowerCase() === email.trim().toLowerCase());
        return { email, contact: matched };
      });

      // Filter out any selected emails that don't match a contact
      const validSelected = selectedContacts.filter(item => item.contact);

      if (validSelected.length === 0) {
        toast.error(isJobContext ? "Recipient email is not a valid job client" : "Recipient email is not a valid lead contact")
        return
      }

      try {
        setIsSending(true)

        // Wrap body HTML in complete HTML document with head tags and styling
        let activeVariables = { ...templateVariables };
        if (validSelected.length === 1 && !isJobContext) {
          const primaryContact = validSelected[0]?.contact;
          const cName = primaryContact?.FullName || primaryContact?.Fullname || primaryContact?.fullName || primaryContact?.UserName || primaryContact?.userName || primaryContact?.name || leadData?.FullName || leadData?.name || "";
          const cEmail = primaryContact?.Email || primaryContact?.email || leadData?.email || leadData?.Email || validSelected[0]?.email || "";
          const cPhone = primaryContact?.PhoneNumber || primaryContact?.phoneNumber || primaryContact?.phone || primaryContact?.Phone || leadData?.phoneNumber || leadData?.phone || "";
          const lName = leadData?.FullName || leadData?.fullName || leadData?.name || cName || "";
          const lEmail = leadData?.email || leadData?.Email || cEmail || "";
          const lPhone = leadData?.phoneNumber || leadData?.phone || cPhone || "";

          activeVariables = {
            FullName: cName || lName,
            Name: cName || lName,
            ContactName: cName || lName,
            LeadName: lName,
            Email: cEmail || lEmail,
            ContactEmail: cEmail || lEmail,
            LeadEmail: lEmail,
            PhoneNumber: cPhone || lPhone,
            Phone: cPhone || lPhone,
            Mobile: cPhone || lPhone,
            ContactPhone: cPhone || lPhone,
            LeadPhone: lPhone,
            CompanyName: leadData?.companyName || leadData?.CompanyName || user?.CompanyName || "",
            CurrentYear: new Date().getFullYear().toString(),
            CurrentDate: new Date().toISOString().split("T")[0],
            ...templateVariables
          };
        }

        const resolvedSubject = replaceTemplateVariables(emailData.subject, activeVariables);
        const resolvedBody = replaceTemplateVariables(emailData.body, activeVariables);
        const completeEmailHtml = ensureCompleteEmailHtml(resolvedBody)

        let result;
        const contactIds = validSelected.map(item => item.contact ? (item.contact.ContactId || item.contact.Id || item.contact.id) : null).filter(Boolean);

        if (validSelected.length > 1 && !isJobContext) {
          // If multiple contacts are selected, use bulk/multiple send helper to package them properly
          const leadsForEmail = validSelected.map(item => ({
            id: leadData.id,
            email: item.email,
            name: item.contact ? (item.contact.Fullname || item.contact.FullName || item.contact.fullName || item.contact.UserName || item.contact.userName || "Unknown") : "Unknown",
            _contactId: item.contact ? (item.contact.ContactId || item.contact.Id || item.contact.id) : null
          }))

          result = await (isJobContext ? sendEmailToMultipleJobs : sendEmailToMultipleLeads)(
            leadsForEmail,
            resolvedSubject,
            completeEmailHtml,
            emailData.cc,
            emailData.bcc,
            emailData.attachments,
            emailService?.ProviderCode,
            selectedTemplate?.TemplateId || selectedTemplate?.templateId || null,
            scheduleForLater ? scheduledDate : null,
            scheduleForLater ? scheduledTime : null,
            contactIds
          );
        } else {
          // Normal single lead send
          const primaryContact = validSelected[0].contact;
          const contactId = primaryContact ? (primaryContact.ContactId || primaryContact.Id || primaryContact.id) : null;

          result = await (isJobContext ? sendEmailToJob : sendEmailToLead)(
            leadData.id,
            emailData.to,
            resolvedSubject,
            completeEmailHtml,
            emailData.cc,
            emailData.bcc,
            emailData.attachments,
            emailService?.ProviderCode,
            selectedTemplate?.TemplateId || selectedTemplate?.templateId || null,
            scheduleForLater ? scheduledDate : null,
            scheduleForLater ? scheduledTime : null,
            contactIds
          );
        }

        if (result.success) {
          toast.success(result.message || (scheduleForLater ? "Email scheduled successfully!" : "Email sent successfully!"))
          resetModalState()
          onClose(true)
        } else {
          throw new Error(result.message || (scheduleForLater ? "Failed to schedule email" : "Failed to send email"))
        }
      } catch (error) {
        console.error("Error sending email:", error)
        let displayError = error.message || (scheduleForLater ? "Failed to schedule email" : "Failed to send email")
        const responseData = error.data || error.response?.data
        if (responseData?.data) {
          try {
            const nestedData = typeof responseData.data === 'string'
              ? JSON.parse(responseData.data)
              : responseData.data
            if (nestedData?.Errors && Array.isArray(nestedData.Errors) && nestedData.Errors.length > 0) {
              displayError = nestedData.Errors.join(', ')
            } else if (nestedData?.errors && Array.isArray(nestedData.errors) && nestedData.errors.length > 0) {
              displayError = nestedData.errors.join(', ')
            }
          } catch (e) {
            // ignore
          }
        } else if (responseData?.errors && typeof responseData.errors === 'object') {
          const errorKeys = Object.keys(responseData.errors)
          if (errorKeys.length > 0) {
            const firstErrorKey = errorKeys[0]
            const errorsForField = responseData.errors[firstErrorKey]
            if (Array.isArray(errorsForField) && errorsForField.length > 0) {
              displayError = errorsForField[0]
            } else if (typeof errorsForField === 'string') {
              displayError = errorsForField
            }
          }
        }
        toast.error(displayError)
      } finally {
        setIsSending(false)
      }
    }
  }


  const handleSendEmail = async () => {
    if (!emailData.subject.trim() || !emailData.body.trim()) {
      toast.error("Please fill in all required fields")
      return
    }

    const totalSize = getAttachmentsTotalSize(emailData.attachments);
    if (totalSize > 20 * 1024 * 1024) {
      toast.error("Total attachments size cannot exceed 20 MB")
      return
    }

    if (scheduleForLater) {
      if (!scheduledDate || !scheduledTime) {
        toast.error("Please select both a date and time for scheduled delivery")
        return
      }
      const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`)
      if (scheduledAt <= new Date()) {
        toast.error("Scheduled date and time must be in the future")
        return
      }
    }

    if (!selectedEmailService) {
      // Open email service selector modal
      setShowEmailServiceSelector(true)
      return
    }

    // Proceed with sending email
    await proceedWithEmailSend(selectedEmailService)
  }

  // The X button always goes through here so a caller opted into confirmOnCancel (e.g. the
  // trader-notification queue) gets a "you haven't sent this" check; other callers close as before.
  const handleRequestClose = () => {
    if (confirmOnCancel) {
      setShowDiscardConfirm(true)
    } else {
      onClose(false)
    }
  }

  const handleConfirmDiscard = () => {
    setShowDiscardConfirm(false)
    onClose(false)
  }

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[200]" onClose={() => { }}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">

              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                {/* KEY FIX: Added h-full to DialogPanel and proper flex layout + remove outline */}
                <DialogPanel
                  onDragOver={(e) => e.preventDefault()}
                  className="relative w-full max-w-2xl h-full max-h-[92vh] min-h-[610px] transform overflow-hidden rounded-lg bg-white shadow-2xl transition-all dark:bg-gray-900 flex flex-col outline-none focus:outline-none"
                >
                  {/* Header - Fixed */}
                  <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8 bg-blue-100 text-blue-700 text-sm font-medium">
                        <EnvelopeIcon className="w-4 h-4" />
                      </Avatar>
                      <div>
                        <DialogTitle as="h3" className="text-lg font-medium text-gray-900 dark:text-white">
                          {isBulkMode && !hideBulkLabels ? "Send Bulk Email" : "Send Email"}
                        </DialogTitle>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {isBulkMode
                            ? `${emailData.toEmails?.length || 0} recipients selected • Remove unwanted emails using × button`
                            : `${leadData?.title || (leadData?.name && leadData.name !== "Unknown Lead" ? leadData.name : null) || "Lead"} - ${emailData.to || "No email"}`
                          }
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleRequestClose}
                      variant="flat"
                      isIcon
                      className="w-8 h-8 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </Button>
                  </div>

                  {/* Tab Navigation - Fixed */}
                  <div className="flex-1 min-h-0 flex flex-col">
                    <TabGroup selectedIndex={activeTab} onChange={setActiveTab} className="flex-1 min-h-0 flex flex-col">
                      <TabList className="flex-shrink-0 flex border-b border-gray-200 dark:border-gray-700 px-6">
                        <Tab className={({ selected }) =>
                          `px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${selected
                            ? 'text-blue-600 border-b-2 border-blue-600'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                          }`
                        }>
                          Compose Message
                        </Tab>
                        <Tab className={({ selected }) =>
                          `px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${selected
                            ? 'text-blue-600 border-b-2 border-blue-600'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                          }`
                        }>
                          Choose Template
                        </Tab>
                      </TabList>

                      {/* KEY FIX: Tab Content with proper height constraints */}
                      <TabPanels className="flex-1 min-h-0 overflow-hidden flex flex-col">
                        {/* Compose Tab */}
                        <TabPanel className="h-full flex flex-col overflow-hidden">
                          <div className="h-full flex flex-col min-h-0">
                            {/* KEY FIX: Scrollable Content with proper flex-1 */}
                            <div className="flex-1 overflow-y-auto min-h-0 custom-scrollbar">
                              <div className="p-6 space-y-4">
                                {/* Email Service Indicator */}
                                {selectedEmailService && (
                                  <div className="flex items-center justify-between bg-emerald-50 dark:bg-emerald-950/20 px-3 py-2 rounded-lg border border-emerald-200 dark:border-emerald-800 text-xs mb-3">
                                    <span className="text-emerald-800 dark:text-emerald-300 font-medium">
                                      Sending via: <strong>{selectedEmailService.ProductName || selectedEmailService.ProviderName}</strong>
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => setShowEmailServiceSelector(true)}
                                      className="text-emerald-700 dark:text-emerald-400 hover:underline font-semibold"
                                    >
                                      Change Service
                                    </button>
                                  </div>
                                )}

                                {/* Template Selection Indicator */}
                                {selectedTemplate && (
                                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                                    <div className="flex items-center gap-2 mb-2">
                                      <DocumentTextIcon className="h-4 w-4 text-blue-600" />
                                      <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                        Using Template: {selectedTemplate.name || selectedTemplate.Name || 'Unnamed Template'}
                                      </span>
                                      <Button
                                        onClick={() => {
                                          setSelectedTemplate(null)
                                          setEmailData(prev => ({ ...prev, subject: '', body: '' }))
                                          setTemplateVariables({})
                                        }}
                                        variant="flat"
                                        size="sm"
                                        className="ml-auto text-xs"
                                      >
                                        Clear Template
                                      </Button>
                                    </div>


                                  </div>
                                )}

                                {/* Email Form */}
                                <div className="space-y-4">

                                  {/* Schedule for later toggle */}
                                  {(!hideSchedule && !isJobUsersContext) && (
                                    <EmailScheduleToggle
                                      enabled={scheduleForLater}
                                      onToggle={() => setScheduleForLater(v => !v)}
                                      scheduledDate={scheduledDate}
                                      onDateChange={setScheduledDate}
                                      scheduledTime={scheduledTime}
                                      onTimeChange={setScheduledTime}
                                      variant="compact"
                                    />
                                  )}
                                  <div className="space-y-1">
                                    <EmailChipsInput
                                      label="To"
                                      emails={emailData.toEmails}
                                      onChange={(emails) => setEmailData(prev => ({ ...prev, toEmails: emails }))}
                                      placeholder={emailData.toEmails.length === 0 ? (hideBulkLabels ? "Type email and press Enter" : "No Contact") : "Lead email addresses will appear as chips here"}
                                    />
                                    {hasNonContactEmail && (
                                      <div className="flex items-center gap-2 text-xs font-semibold text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/30 rounded-lg p-2.5 mt-1.5 animate-in fade-in slide-in-from-top-1">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        <span>This email will only be sent to contacts.</span>
                                      </div>
                                    )}
                                    {emailData.toEmails.length === 0 && (
                                      <div className="flex items-center gap-2 text-xs font-semibold text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/30 rounded-lg p-2.5 mt-1.5">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        <span>{hideBulkLabels ? "No recipients selected. Please add email addresses above." : "No Contact: Lead doesn't have any contacts assigned"}</span>
                                      </div>
                                    )}
                                  </div>

                                  {/* CC/BCC Toggle Buttons */}
                                  {(!showCc || !showBcc) && (
                                    <div className="flex gap-2">
                                      {!showCc && (
                                        <Button
                                          type="button"
                                          variant="outlined"
                                          size="sm"
                                          onClick={() => setShowCc(true)}
                                          className="text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                        >
                                          <PlusIcon className="w-3 h-3 mr-1" />
                                          Add CC
                                        </Button>
                                      )}
                                      {!showBcc && (
                                        <Button
                                          type="button"
                                          variant="outlined"
                                          size="sm"
                                          onClick={() => setShowBcc(true)}
                                          className="text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                        >
                                          <PlusIcon className="w-3 h-3 mr-1" />
                                          Add BCC
                                        </Button>
                                      )}
                                    </div>
                                  )}

                                  {/* CC Field */}
                                  {showCc && (
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                          CC
                                        </label>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setShowCc(false)
                                            setEmailData(prev => ({ ...prev, cc: [] }))
                                          }}
                                          className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors flex items-center gap-1"
                                        >
                                          <XMarkIcon className="w-3 h-3" />
                                          Remove CC
                                        </button>
                                      </div>
                                      <EmailChipsInput
                                        label=""
                                        emails={emailData.cc}
                                        onChange={(emails) => setEmailData(prev => ({ ...prev, cc: emails }))}
                                        placeholder="Add CC recipients (press Enter or comma)"
                                      />
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        CC recipients will see each other&apos;s email addresses
                                      </p>
                                    </div>
                                  )}

                                  {/* BCC Field */}
                                  {showBcc && (
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                          BCC
                                        </label>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setShowBcc(false)
                                            setEmailData(prev => ({ ...prev, bcc: [] }))
                                          }}
                                          className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors flex items-center gap-1"
                                        >
                                          <XMarkIcon className="w-3 h-3" />
                                          Remove BCC
                                        </button>
                                      </div>
                                      <EmailChipsInput
                                        label=""
                                        emails={emailData.bcc}
                                        onChange={(emails) => setEmailData(prev => ({ ...prev, bcc: emails }))}
                                        placeholder="Add BCC recipients (press Enter or comma)"
                                      />
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        BCC recipients are hidden from other recipients
                                      </p>
                                    </div>
                                  )}

                                  <Input
                                    label="Subject"
                                    value={emailData.subject}
                                    onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                                    placeholder="Enter email subject"
                                    required
                                    autoFocus
                                  />


                                  {/* File Attachments */}
                                  <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Attachments {emailData.attachments.length > 0 && (
                                          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                                            ({emailData.attachments.length} file{emailData.attachments.length !== 1 ? 's' : ''} • Total: {formatFileSize(getAttachmentsTotalSize(emailData.attachments))})
                                          </span>
                                        )}
                                      </label>
                                      {emailData.attachments.length > 0 && (
                                        <button
                                          type="button"
                                          onClick={() => setEmailData(prev => ({ ...prev, attachments: [] }))}
                                          className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors"
                                        >
                                          Clear All
                                        </button>
                                      )}
                                    </div>

                                    <div
                                      onDragOver={handleDragOver}
                                      onDragEnter={handleDragEnter}
                                      onDragLeave={handleDragLeave}
                                      onDrop={handleDrop}
                                      className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-dashed transition-all duration-200 ${
                                        isDragging
                                          ? "border-blue-500 bg-blue-50/70 dark:bg-blue-900/20 ring-2 ring-blue-400/30"
                                          : "border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/40 hover:border-blue-400 dark:hover:border-blue-500"
                                      }`}
                                    >
                                      <input
                                        type="file"
                                        multiple
                                        onChange={(e) => {
                                          handleAddFiles(e.target.files)
                                          e.target.value = '' // Reset input
                                        }}
                                        className="hidden"
                                        id="file-upload"
                                      />
                                      <label
                                        htmlFor="file-upload"
                                        className="cursor-pointer inline-flex items-center px-3.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-blue-50 hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:border-blue-500 transition-all duration-200 shrink-0 shadow-sm"
                                      >
                                        <PaperClipIcon className="w-4 h-4 mr-1.5 text-gray-500 dark:text-gray-400" />
                                        Choose Files
                                      </label>
                                      <span className={`text-xs transition-colors duration-150 ${isDragging ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-500 dark:text-gray-400"}`}>
                                        {isDragging ? "Drop files here to attach..." : "or drag and drop files here"}
                                      </span>
                                    </div>

                                    {/* Attachment List */}
                                    {emailData.attachments.length > 0 && (
                                      <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar p-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                                        {emailData.attachments.map((file, index) => (
                                          <div
                                            key={index}
                                            className="group flex items-center justify-between p-2.5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-sm transition-all duration-200"
                                          >
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                                <PaperClipIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                              </div>
                                              <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 ">
                                                  {file.name || file}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                  {formatFileSize(file.size || file.FileSize || file.fileSize || 0)}
                                                </p>
                                              </div>
                                            </div>
                                            <div className="ml-3 flex-shrink-0 flex items-center gap-1">
                                              <button
                                                type="button"
                                                onClick={() => handleViewAttachment(file)}
                                                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 rounded-lg transition-all duration-200"
                                                title="View attachment"
                                              >
                                                <EyeIcon className="w-4 h-4" />
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setEmailData(prev => ({
                                                    ...prev,
                                                    attachments: prev.attachments.filter((_, i) => i !== index)
                                                  }))
                                                  toast.success('File removed')
                                                }}
                                                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:bg-red-900/20 dark:hover:text-red-400 rounded-lg transition-all duration-200"
                                                title="Remove file"
                                              >
                                                <XMarkIcon className="w-4 h-4" />
                                              </button>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      Tip: You can attach multiple files at once
                                    </p>
                                  </div>


                                  {/* Rich Text Editor for Message */}
                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between mb-1.5">
                                      <label className="input-label flex items-center gap-1">
                                        <span className="input-label-text">Message</span>
                                        <span className="text-error">*</span>
                                      </label>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outlined"
                                        onClick={() => setShowVariablesModal(true)}
                                        className="text-xs py-1 px-2"
                                      >
                                        Manage Variables
                                      </Button>
                                    </div>
                                    <RichTextEditor
                                      value={emailData.body}
                                      onChange={(content) => setEmailData(prev => ({ ...prev, body: content }))}
                                      placeholder="Compose your professional email with rich formatting..."
                                      height="300px"
                                      onImageInsert={() => setShowImageModal(true)}
                                      imageUrlToInsert={imageUrl}
                                      imageWidth={imageWidth}
                                      imageHeight={imageHeight}
                                      onImageEdit={handleImageEdit}
                                      editingImage={editingImage}
                                      onHtmlInsert={() => {
                                        setHtmlContent("")
                                        setShowHtmlModal(true)
                                      }}
                                      htmlContentToInsert={htmlContentToInsert}
                                      required
                                    />
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {emailData.body.replace(/<[^>]*>/g, '').length} characters (HTML content)
                                    </div>
                                  </div>
                                </div>

                              </div>
                            </div>

                            {/* Send Button - Only visible in Compose tab */}
                            <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-6 py-4.5">
                              <div className="flex justify-end items-center">
                                <Button
                                  onClick={handleSendEmail}
                                  disabled={emailData.toEmails.length === 0 || !emailData.subject.trim() || !emailData.body.trim() || isSending}
                                  variant="solid"
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-6"
                                >
                                  {isSending ? (
                                    <>
                                      <Spinner className="w-4 h-4 mr-2" />
                                      Sending...
                                    </>
                                  ) : (
                                    <>
                                      <PaperAirplaneIcon className="w-4 h-4 mr-2" />
                                      {scheduleForLater
                                        ? (isBulkMode && !hideBulkLabels ? `Schedule for ${emailData.toEmails.length} Recipients` : "Schedule Email")
                                        : (isBulkMode && !hideBulkLabels ? `Send to ${emailData.toEmails.length} Recipients` : "Send Email")}
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </TabPanel>

                        {/* Templates Tab */}
                        <TabPanel className="h-full flex flex-col overflow-hidden">
                          <div className="h-full flex flex-col min-h-0">
                            {/* Search - Fixed */}
                            <div className="flex-shrink-0 p-6 pb-2">
                              <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                  value={templateSearch}
                                  onChange={(e) => handleTemplateSearch(e.target.value)}
                                  placeholder="Search email templates..."
                                  className="pl-10"
                                />
                              </div>
                            </div>

                            {/* Templates List - Clean Design */}
                            <div className="flex-1 overflow-y-auto min-h-0 px-6 pb-6 custom-scrollbar">
                              {templatesLoading ? (
                                <div className="flex items-center justify-center py-8 h-full">
                                  <div className="text-center">
                                    <Spinner className="h-6 w-6 mx-auto mb-2" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Loading templates...</span>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-3 pb-4 min-h-full">
                                  {templates.length === 0 ? (
                                    <div className="flex items-center justify-center h-full">
                                      <div className="text-center py-8">
                                        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                                          No templates found
                                        </h3>
                                        <p className="text-gray-500 dark:text-gray-400">
                                          {templateSearch ? "Try a different search term" : "No email templates available"}
                                        </p>
                                      </div>
                                    </div>
                                  ) : (
                                    templates.map((template) => (
                                      <div
                                        key={template.id}
                                        className="border border-gray-200 bg-white rounded-md p-3 hover:border-blue-500 hover:bg-blue-50/50 transition-colors dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-blue-900/10"
                                      >
                                        {/* Header with title and button */}
                                        <div className="flex items-center justify-between gap-2 mb-2">
                                          <div className="flex-1 min-w-0">
                                            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 ">
                                              {template.name || template.Name || 'Unnamed Template'}
                                            </h4>
                                          </div>
                                          <Button
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleTemplateSelect(template)
                                            }}
                                            variant="outlined"
                                            size="sm"
                                            className="flex-shrink-0 px-2 py-1 text-xs"
                                          >
                                            Use Template
                                          </Button>
                                        </div>

                                        {/* Subject line */}
                                        {(template.subject || template.Subject) && (
                                          <div className="mb-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                              <span className="font-medium text-gray-900 dark:text-gray-100">Subject:</span> {template.subject || template.Subject}
                                            </p>
                                          </div>
                                        )}

                                        {/* Body preview - sandboxed to prevent global CSS leakage */}
                                        <div className="mb-2">
                                          <IframePreview html={template.body || template.Body || ''} />
                                        </div>

                                        {/* Template Attachments Display - NEW SECTION */}
                                        {/* Template Attachments Display - FIXED VERSION */}
                                        {template.attachments && template.attachments.length > 0 && (
                                          <div className="mb-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                            <div className="flex items-center gap-2 mb-2">
                                              <PaperClipIcon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                {template.attachments.length} Attachment{template.attachments.length !== 1 ? 's' : ''}
                                              </span>
                                            </div>
                                            <div className="space-y-1.5">
                                              {template.attachments.map((attachment, idx) => {
                                                // Safely extract values to prevent object rendering errors
                                                const fileName = attachment.FileName || attachment.fileName || `File ${idx + 1}`;
                                                const fileSize = attachment.FileSize || attachment.fileSize;

                                                return (
                                                  <div
                                                    key={idx}
                                                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                                                  >
                                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                                      <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded flex items-center justify-center">
                                                        <PaperClipIcon className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                                      </div>
                                                      <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 ">
                                                          {fileName}
                                                        </p>
                                                        {fileSize && (
                                                          <p className="text-[10px] text-gray-500 dark:text-gray-400">
                                                            {(fileSize / 1024).toFixed(1)} KB
                                                          </p>
                                                        )}
                                                      </div>
                                                    </div>
                                                    <button
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleViewAttachment(attachment);
                                                      }}
                                                      className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-100 dark:text-gray-400 dark:hover:bg-blue-900/30 rounded transition-colors"
                                                      title="View attachment"
                                                    >
                                                      <EyeIcon className="w-3.5 h-3.5" />
                                                    </button>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}


                                        {/* Variables badges */}
                                        {extractTemplateVariables(template.body || template.Body || '').length > 0 && (
                                          <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-200 dark:border-gray-700">
                                            {extractTemplateVariables(template.body || template.Body || '').map((variable) => (
                                              <Badge key={variable} variant="soft" color="primary" className="text-[10px]">
                                                {`{{${variable}}}`}
                                              </Badge>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </TabPanel>
                      </TabPanels>
                    </TabGroup>
                  </div>

                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Image Insert Modal */}
      <Transition appear show={showImageModal} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-[9999]"
          onClose={() => { }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
          </TransitionChild>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative flex w-full max-w-md origin-center flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-gray-800 outline-none focus:outline-none">
                {/* Header */}
                <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 sm:px-5 dark:bg-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 shadow-md dark:bg-blue-900">
                      <EnvelopeIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <DialogTitle
                      as="h3"
                      className="text-base font-medium text-gray-800 dark:text-gray-100"
                    >
                      {editingImage ? 'Edit Image' : 'Insert Image'}
                    </DialogTitle>
                  </div>
                  <Button
                    onClick={handleImageModalClose}
                    variant="ghost"
                    size="sm"
                    className="rounded-full p-1"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </Button>
                </div>

                {/* Body */}
                <div className="flex flex-col overflow-y-auto px-4 py-4 sm:px-5">
                  <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                    Enter the image URL and dimensions.
                  </p>

                  <div className="space-y-4">
                    <Input
                      label="Image URL"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      type="url"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Width (px)"
                        value={imageWidth}
                        onChange={(e) => setImageWidth(e.target.value)}
                        placeholder="300"
                        type="number"
                      />
                      <Input
                        label="Height (px)"
                        value={imageHeight}
                        onChange={(e) => setImageHeight(e.target.value)}
                        placeholder="200"
                        type="number"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-4 flex justify-end space-x-3">
                    <Button
                      type="button"
                      onClick={handleImageModalClose}
                      variant="outlined"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleImageInsert}
                      disabled={!imageUrl.trim()}
                      color="primary"
                    >
                      {editingImage ? 'Update' : 'Insert'} Image
                    </Button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      {/* HTML Content Modal */}
      <Transition appear show={showHtmlModal} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-[9999]"
          onClose={() => { }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
          </TransitionChild>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative flex w-full max-w-2xl origin-center flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-gray-800 outline-none focus:outline-none">
                {/* Header */}
                <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 sm:px-5 dark:bg-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 shadow-md dark:bg-blue-900">
                      <EnvelopeIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <DialogTitle
                      as="h3"
                      className="text-base font-medium text-gray-800 dark:text-gray-100"
                    >
                      Insert HTML Content
                    </DialogTitle>
                  </div>
                  <Button
                    onClick={handleHtmlModalClose}
                    variant="ghost"
                    size="sm"
                    className="rounded-full p-1"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </Button>
                </div>

                {/* Body */}
                <div className="flex flex-col overflow-y-auto px-4 py-4 sm:px-5">
                  <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                    Enter your HTML content to insert into the email.
                  </p>

                  <div className="space-y-4">
                    <Textarea
                      label="HTML Content"
                      value={htmlContent}
                      onChange={(e) => setHtmlContent(e.target.value)}
                      placeholder="<p>Enter your HTML content here...</p>"
                      rows={10}
                      className="font-mono text-sm"
                    />
                  </div>

                  {/* Actions */}
                  <div className="mt-4 flex justify-end space-x-3">
                    <Button
                      type="button"
                      onClick={handleHtmlModalClose}
                      variant="outlined"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleHtmlInsert}
                      disabled={!htmlContent.trim()}
                      color="primary"
                    >
                      Insert HTML
                    </Button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      {/* Email Service Selector Modal */}
      <EmailServiceSelector
        isOpen={showEmailServiceSelector}
        onClose={() => setShowEmailServiceSelector(false)}
        onSelect={(service) => {
          setSelectedEmailService(service)
          // Automatically proceed with sending after service selection
          setTimeout(() => {
            proceedWithEmailSend(service)
          }, 100)
        }}
        selectedService={selectedEmailService}
        title="Select Email Service"
        description="Choose which email service to use for sending this message"
      />

      {/* View Document Modal */}
      <ViewDocumentModal
        isOpen={viewDocModalOpen}
        onClose={() => {
          setViewDocModalOpen(false)
          setSelectedDocToView(null)
        }}
        document={selectedDocToView}
      />

      {/* Discard confirmation — only shown when the caller opts in via confirmOnCancel */}
      <ConfirmModal
        show={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        onOk={handleConfirmDiscard}
        state="pending"
        buttonColor="error"
        messages={{
          pending: {
            title: "Discard this email?",
            description: discardWarningMessage,
            actionText: "Discard"
          }
        }}
      />

      <ManageVariablesModal
        isOpen={showVariablesModal}
        onClose={() => setShowVariablesModal(false)}
        onInsert={(variableName) => {
          setHtmlContentToInsert(`{{${variableName}}}`);
          setShowVariablesModal(false);
        }}
      />
    </>
  )
}

export default EmailMessageModal
